


import React, { useState, useEffect, useRef } from "react";
import { message } from "antd";
import courseService from "../../services/course.service";
import PaymentWrapper from "../payment/PaymentWrapper";

/* ================================================================
   Landing Page SAP S/4 HANA — V6 with Search
   ================================================================ */

const FocusModal = React.memo(function FocusModal({ visible, onClose, title, children }) {
  const box = useRef(null);
  useEffect(() => { if (visible && box.current) { const el = box.current.querySelector("button, input, a"); el?.focus(); } }, [visible]);
  if (!visible) return null;
  return (
    <div role="dialog" aria-modal="true"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 16, backdropFilter: "blur(3px)" }}
      onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div ref={box} style={{ background: "white", borderRadius: 12, padding: 24, width: "min(440px,94vw)", boxShadow: "0 16px 40px rgba(0,0,0,0.18)" }}>
        {title && <h3 style={{ margin: "0 0 14px", fontSize: 16, color: "#111", fontWeight: 700 }}>{title}</h3>}
        {children}
      </div>
    </div>
  );
});

const SapLandingPage = () => {
  /* ---- scroll animation ---- */
  const [vis, setVis] = useState(new Set());
  const refs = useRef({});
  // useEffect(() => {
  //   const obs = new IntersectionObserver(ee => ee.forEach(e => e.isIntersecting && setVis(p => new Set([...p, e.target.id]))), { threshold: 0.1 });
  //   Object.values(refs.current).forEach(el => el && obs.observe(el));
  //   return () => obs.disconnect();
  // }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(ee => ee.forEach(e => e.isIntersecting && setVis(p => new Set([...p, e.target.id]))), { threshold: 0.1 });
    Object.values(refs.current).forEach(el => el && obs.observe(el));
    const t = setTimeout(() => { setVis(new Set(Object.keys(refs.current))); }, 1000);
    return () => { obs.disconnect(); clearTimeout(t); };
  }, []);
  const R = id => el => { refs.current[id] = el; };
  const V = id => vis.has(id);
  const A = (id, i = 0) => ({ opacity: V(id) ? 1 : 0, transform: V(id) ? "translateY(0)" : "translateY(16px)", transition: `all .5s cubic-bezier(.16,1,.3,1) ${i * .07}s` });

  /* ---- billing ---- */
  const [period, setPeriod] = useState('monthly');
  const PER = [{ k: 'monthly', l: 'Mensuel' }, { k: 'quarterly', l: 'Trimestriel' }, { k: 'semiannual', l: 'Semestriel' }, { k: 'annual', l: 'Annuel' }];
  const MUL = { monthly: 1, quarterly: 3, semiannual: 6, annual: 12 };
  const LBL = { monthly: 'mois', quarterly: 'trimestre', semiannual: 'semestre', annual: 'an' };
  const calc = (p, per) => { const m = Number(p.price), mul = MUL[per], yr = m * 12; let t = m * mul; if (per === 'annual') t = yr * (1 - Number(p.annualDiscountRate ?? 0)); return { t, lbl: LBL[per], yr }; };
  const normC = c => { if (!c) return 'USD'; const u = String(c).trim().toUpperCase(); if (u === '$') return 'USD'; if (u === '€') return 'EUR'; if (['DH', 'د.م.', 'MAD'].includes(u)) return 'MAD'; return /^[A-Z]{3}$/.test(u) ? u : 'USD'; };
  const F = (v, c) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: normC(c) }).format(Number(v || 0));

  /* ---- plans ---- */
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => { (async () => { try { const d = await courseService.getAllPlans(); setPlans(Array.isArray(d) ? d : []); } catch { setErr("Impossible de charger les offres."); } finally { setLoading(false); } })(); }, []);

  /* ---- search ---- */
  const [searchQuery, setSearchQuery] = useState("");
  const filteredPlans = plans.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const titleMatch = (p.title || "").toLowerCase().includes(q);
    const featuresMatch = (p.features || []).some(f => f.toLowerCase().includes(q));
    return titleMatch || featuresMatch;
  });

  /* ---- achat ---- */
  const [sel, setSel] = useState(null);
  const [cfmVis, setCfmVis] = useState(false);
  const [payId, setPayId] = useState(null);
  const [payVis, setPayVis] = useState(false);
  const [payData, setPayData] = useState(null);

  const buy = p => { setSel(p); setCfmVis(true); setPayId(p?.id); };
  const cfm = () => {
    if (!sel) return;
    const { t } = calc(sel, period);
    setPayData({ id: sel.id, title: `${sel.title} — ${LBL[period]}`, latest_price: +t.toFixed(2), price: +t.toFixed(2), currency: sel.currency || "USD", metadata: { billingPeriod: period, annualDiscountRate: +(sel.annualDiscountRate ?? 0) } });
    setCfmVis(false); setPayVis(true);
  };
  const onOk = res => {
    const { paymentIntent, ticket, moneris } = res || {};
    message.success(paymentIntent ? "🎉 Paiement réussi !" : (ticket || moneris) ? "🎉 Paiement initialisé." : "🎉 Paiement terminé.");
    setPayVis(false); setPayData(null); setSel(null); setPayId(null);
  };
  const onErr = e => { console.error(e); message.error(e?.message || "Erreur paiement."); };
  const onX = () => { setPayVis(false); setPayData(null); };

  const CL = ["#0054A6", "#00875A", "#E85D04", "#7C3AED", "#DC2626", "#0891B2"];
  const gc = (p, i) => p.color || CL[i % CL.length];
  const [openFaq, setOpenFaq] = useState(null);

  /* ---- animated counter ---- */
  const CountUp = ({ target, suffix = "" }) => {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);
    useEffect(() => {
      const obs = new IntersectionObserver(([e]) => {
        if (e.isIntersecting && !started.current) {
          started.current = true;
          const num = parseFloat(target);
          const dur = 1200;
          const step = 16;
          const inc = num / (dur / step);
          let cur = 0;
          const timer = setInterval(() => {
            cur += inc;
            if (cur >= num) { cur = num; clearInterval(timer); }
            setVal(cur);
          }, step);
        }
      }, { threshold: 0.3 });
      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, [target]);
    const display = Number.isInteger(parseFloat(target)) ? Math.round(val) : val.toFixed(1);
    return <span ref={ref}>{display}{suffix}</span>;
  };

  return (
    <div style={{ fontFamily: "'Outfit',sans-serif", background: "#fff", color: "#1a1a2e", overflowX: "hidden", fontSize: 14 }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      {/* ==== NAV ==== */}
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid #eee", padding: "0 20px" }}>
        <div style={{ maxWidth: 1060, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 50 }}>
          <a href="/home" style={{ display: "flex", alignItems: "center" }}>
            <img src="/img/nomidya.png" alt="Nomidya" style={{ height: 32 }} />
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            {[["features", "Avantages"], ["pricing", "Tarifs"], ["faq", "FAQ"]].map(([id, lb]) => (
              <a key={id} href={`#${id}`} style={{ color: "#666", textDecoration: "none", fontSize: 12, fontWeight: 500 }}
                onMouseEnter={e => e.target.style.color = "#111"} onMouseLeave={e => e.target.style.color = "#666"}>{lb}</a>
            ))}
            <a href="/home" style={{ color: "#666", textDecoration: "none", fontSize: 12, fontWeight: 500 }}
              onMouseEnter={e => e.target.style.color = "#111"} onMouseLeave={e => e.target.style.color = "#666"}>Accueil</a>
            <a href="#pricing" style={{ padding: "6px 14px", borderRadius: 6, background: "#E85D04", color: "white", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>Commencer</a>
          </div>
        </div>
      </nav>

      {/* ==== HERO ==== */}
      <section id="hero" ref={R("hero")} style={{
        paddingTop: 50, minHeight: "75vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(160deg,#0a1628 0%,#122a45 50%,#0a1628 100%)", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, opacity: .04, backgroundImage: "linear-gradient(rgba(255,255,255,.15) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.15) 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
        <div style={{ position: "absolute", top: "-12%", right: "-6%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,rgba(0,135,90,.1) 0%,transparent 70%)", filter: "blur(40px)" }} />
        <div style={{ position: "absolute", bottom: "-8%", left: "-3%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(232,93,4,.08) 0%,transparent 70%)", filter: "blur(40px)" }} />

        <div style={{ position: "relative", zIndex: 2, textAlign: "center", maxWidth: 600, padding: "40px 20px", ...A("hero") }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 14px", borderRadius: 20, background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.08)", marginBottom: 16 }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#00C853" }} />
            <span style={{ color: "rgba(255,255,255,.65)", fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase" }}>Environnements disponibles</span>
          </div>

          <h1 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: "clamp(24px,4vw,40px)", fontWeight: 800, color: "white", lineHeight: 1.15, margin: "0 0 12px", letterSpacing: "-.01em" }}>
            Accès <span style={{ background: "linear-gradient(135deg,#FF8C00,#E85D04)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>SAP S/4 HANA</span>
            <br />dédié & professionnel
          </h1>

          <p style={{ fontSize: 14, color: "rgba(255,255,255,.45)", lineHeight: 1.6, maxWidth: 460, margin: "0 auto 20px" }}>
            Environnement SAP complet, réservé exclusivement à vous. Pratiquez et testez en conditions réelles.
          </p>

          {/* Garantie badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 16px", borderRadius: 20, background: "rgba(82,196,26,.1)", border: "1px solid rgba(82,196,26,.2)", marginBottom: 20 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#52c41a"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" /></svg>
            <span style={{ color: "#52c41a", fontSize: 11, fontWeight: 600 }}>Satisfait ou remboursé — 14 jours</span>
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#pricing" style={{ padding: "10px 24px", borderRadius: 8, background: "linear-gradient(135deg,#FF8C00,#E85D04)", color: "white", fontWeight: 700, fontSize: 13, textDecoration: "none", boxShadow: "0 4px 20px rgba(232,93,4,.25)" }}>Voir les offres →</a>
            <a href="#how" style={{ padding: "10px 24px", borderRadius: 8, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.7)", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>Comment ça marche</a>
          </div>
        </div>
      </section>

      {/* ==== STATS ==== */}
      <section id="stats" ref={R("stats")} style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", background: "white", borderBottom: "1px solid #f0f0f0" }}>
        {[["500", "+", "Professionnels formés"], ["99.5", "%", "Disponibilité"], ["24", "/7", "Support technique"], ["15", "+", "Modules SAP"]].map(([num, suf, lb], i) => (
          <div key={i} style={{ padding: "18px 16px", textAlign: "center", borderRight: i < 3 ? "1px solid #f0f0f0" : "none", ...A("stats", i) }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 800, color: "#0054A6" }}>
              <CountUp target={num} suffix={suf} />
            </div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{lb}</div>
          </div>
        ))}
      </section>

      {/* ==== COMMENT ÇA MARCHE ==== */}
      <section id="how" ref={R("how")} style={{ padding: "44px 20px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28, ...A("how") }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#E85D04", letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Simple & rapide</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 24, fontWeight: 800, color: "#111", margin: 0 }}>Comment ça marche</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, position: "relative" }}>
          <div style={{ position: "absolute", top: 40, left: "20%", right: "20%", height: 2, background: "linear-gradient(90deg,#E85D04,#0054A6,#00875A)", opacity: .15, zIndex: 0 }} />
          {[
            { step: "1", icon: "📋", title: "Choisissez votre plan", desc: "Sélectionnez l'offre SAP adaptée à vos besoins et votre budget.", color: "#E85D04" },
            { step: "2", icon: "💳", title: "Paiement sécurisé", desc: "Réglez en ligne via notre plateforme de paiement sécurisée Moneris.", color: "#0054A6" },
            { step: "3", icon: "🚀", title: "Accès en 24h", desc: "Recevez vos identifiants par email et commencez immédiatement.", color: "#00875A" },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: "center", position: "relative", zIndex: 1, padding: 20, ...A("how", i) }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${s.color}10`, border: `2px solid ${s.color}25`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", fontSize: 22, position: "relative" }}>
                {s.icon}
                <div style={{ position: "absolute", top: -4, right: -4, width: 20, height: 20, borderRadius: "50%", background: s.color, color: "white", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.step}</div>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 4 }}>{s.title}</div>
              <div style={{ fontSize: 12, color: "#888", lineHeight: 1.5 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ==== FEATURES ==== */}
      <section id="features" ref={R("features")} style={{ padding: "44px 20px", maxWidth: 1060, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 28, ...A("features") }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#E85D04", letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Pourquoi Nomidya</span>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 24, fontWeight: 800, color: "#111", margin: 0 }}>Un environnement SAP taillé pour vous</h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
          {[
            ["🔒", "Accès 100% dédié", "Environnement isolé et sécurisé, aucun partage de données.", "#0054A6"],
            ["⚡", "Haute performance", "Base HANA optimisée, disponibilité garantie 99.5%.", "#00875A"],
            ["🛠️", "Tous les modules", "FI, CO, MM, SD, PP, PM, QM — tous activés avec données de démo.", "#E85D04"],
            ["📱", "Fiori & GUI", "SAP GUI classique et interface Fiori depuis votre navigateur.", "#7C3AED"],
            ["💻", "ABAP & CDS", "ABAP Workbench, Eclipse ADT, RAP Framework et CDS Views.", "#DC2626"],
            ["🎓", "Support technique", "Équipe de support disponible pour vous accompagner.", "#0891B2"],
          ].map(([ic, ti, desc, col], i) => (
            <div key={i} style={{ padding: 18, borderRadius: 10, background: "white", border: "1px solid #f0f0f0", position: "relative", overflow: "hidden", ...A("features", i) }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,0,0,.05)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: col }} />
              <div style={{ width: 34, height: 34, borderRadius: 8, background: col, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, marginBottom: 10 }}>{ic}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 3 }}>{ti}</div>
              <div style={{ fontSize: 12, color: "#777", lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ==== PRICING ==== */}
      <section id="pricing" ref={R("pricing")} style={{ padding: "44px 12px", background: "#f7f8fa" }}>
        <div style={{ maxWidth: 1440, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 28, ...A("pricing") }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#E85D04", letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Tarification</span>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 24, fontWeight: 800, color: "#111", margin: "0 0 4px" }}>Choisissez votre plans</h2>
            <p style={{ fontSize: 13, color: "#888", margin: "0 auto 16px", maxWidth: 380 }}>Tarifs transparents, sans engagement. Annulez quand vous voulez.</p>

            {/* Billing toggle */}
            <div style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 8, background: "white", border: "1px solid #e5e7eb", marginBottom: 16 }}>
              {PER.map(p => (
                <button key={p.k} onClick={() => setPeriod(p.k)} style={{
                  padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontFamily: "'Outfit'",
                  background: period === p.k ? "#0a1628" : "transparent",
                  color: period === p.k ? "white" : "#888", fontWeight: 600, fontSize: 11, transition: "all .2s",
                }}>
                  {p.l}
                  {p.k === "annual" && <span style={{ marginLeft: 3, padding: "1px 5px", borderRadius: 8, background: "rgba(232,93,4,.1)", color: "#E85D04", fontSize: 9, fontWeight: 700 }}>-20%</span>}
                </button>
              ))}
            </div>

            {/* Search bar */}
            <div style={{ maxWidth: 420, margin: "0 auto" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 14px", borderRadius: 10,
                background: "white", border: "1px solid #e5e7eb",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#bbb">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher un accès SAP (ex: ABAP, Fiori, Adobe Forms...)"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    flex: 1, border: "none", outline: "none", fontSize: 13,
                    fontFamily: "'Outfit',sans-serif", color: "#333",
                    background: "transparent",
                  }}
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery("")}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "#ccc", fontSize: 16, padding: 0, lineHeight: 1 }}>
                    ×
                  </button>
                )}
              </div>
              {searchQuery && (
                <p style={{ fontSize: 11, color: "#888", marginTop: 6 }}>
                  {filteredPlans.length} résultat{filteredPlans.length !== 1 ? "s" : ""} pour « <strong>{searchQuery}</strong> »
                </p>
              )}
            </div>
          </div>

          {loading && (
            <div>
              <div className="lp-plans-grid" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, alignItems: "stretch", maxWidth: 1400, margin: "0 auto" }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} style={{
                    borderRadius: 10, background: "white", overflow: "hidden", border: "1px solid #eaeaea",
                    animation: "pulse 1.5s ease-in-out infinite",
                  }}>
                    <div style={{ height: 80, background: "linear-gradient(135deg,#e0e7ef,#d0d8e4)" }} />
                    <div style={{ padding: "16px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                      <div style={{ width: "60%", height: 24, borderRadius: 6, background: "#eef1f5" }} />
                      <div style={{ width: "80%", height: 12, borderRadius: 4, background: "#eef1f5" }} />
                      <div style={{ width: "100%", height: 36, borderRadius: 8, background: "linear-gradient(135deg,#e0e7ef,#d0d8e4)" }} />
                      <div style={{ width: "50%", height: 10, borderRadius: 4, background: "#eef1f5" }} />
                      <div style={{ width: "100%", padding: 10, background: "#fafafa", borderRadius: 8 }}>
                        <div style={{ width: "70%", height: 8, borderRadius: 4, background: "#eef1f5", marginBottom: 6 }} />
                        <div style={{ width: "90%", height: 8, borderRadius: 4, background: "#eef1f5", marginBottom: 6 }} />
                        <div style={{ width: "60%", height: 8, borderRadius: 4, background: "#eef1f5" }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ textAlign: "center", marginTop: 20 }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "10px 24px", borderRadius: 10, background: "white", border: "1px solid #e5e7eb" }}>
                  <div style={{
                    width: 18, height: 18, border: "2.5px solid #e0e7ef", borderTopColor: "#0054A6",
                    borderRadius: "50%", animation: "spin 0.8s linear infinite",
                  }} />
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>Chargement des offres…</span>
                </div>
              </div>
            </div>
          )}
          {!loading && err && <div style={{ textAlign: "center", padding: 24, color: "#ef4444", fontSize: 13 }}>{err}</div>}

          {!loading && !err && filteredPlans.length === 0 && searchQuery && (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
              <p style={{ fontSize: 14, color: "#888", marginBottom: 4 }}>Aucun accès trouvé pour « <strong>{searchQuery}</strong> »</p>
              <p style={{ fontSize: 12, color: "#bbb" }}>Essayez avec d'autres mots-clés ou <button onClick={() => setSearchQuery("")} style={{ border: "none", background: "none", color: "#0054A6", cursor: "pointer", fontWeight: 600, fontSize: 12, textDecoration: "underline" }}>voir tous les accès</button></p>
            </div>
          )}

          {!loading && !err && filteredPlans.length > 0 && (
            <div className="lp-plans-grid" style={{ display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, alignItems: "stretch", maxWidth: 1400, margin: "0 auto" }}>
              {filteredPlans.map((plan, i) => {
                const { t, lbl, yr } = calc(plan, period);
                const rate = Number(plan.annualDiscountRate ?? 0);
                const sav = Math.max(0, yr - t);
                const zero = t === 0 || Number(plan.price) === 0;
                const c = gc(plan, i);
                const pop = i === 1 && !searchQuery;

                return (
                  <div key={plan.id} style={{
                    borderRadius: 10, background: "white", overflow: "hidden", display: "flex", flexDirection: "column", position: "relative",
                    border: pop ? `2px solid ${c}` : "1px solid #eaeaea",
                    boxShadow: pop ? `0 10px 36px ${c}14` : "0 1px 8px rgba(0,0,0,.03)",
                    transform: pop ? "scale(1.02)" : "scale(1)", ...A("pricing", i),
                  }}>
                    {plan.isNew && <div style={{ position: "absolute", top: 14, right: -26, background: "#ff4d4f", color: "white", padding: "2px 32px", transform: "rotate(45deg)", fontSize: 9, fontWeight: 700, zIndex: 1 }}>NEW</div>}
                    {pop && <div style={{ background: c, color: "white", textAlign: "center", padding: "5px 0", fontSize: 10, fontWeight: 700, letterSpacing: .5 }}>⭐ POPULAIRE</div>}

                    <div style={{ padding: "14px 10px", textAlign: "center", background: `linear-gradient(135deg,${c},${c}dd)` }}>
                      <div style={{ width: 32, height: 32, margin: "0 auto 4px", backgroundColor: "rgba(255,255,255,.18)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" /></svg>
                      </div>
                      <div style={{ color: "white", fontSize: 11, lineHeight: 1.3, fontWeight: 600 }}>{plan.title}</div>
                    </div>

                    <div style={{ padding: "12px 10px", flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                      {!zero && (
                        <div style={{ textAlign: "center", marginBottom: 8 }}>
                          <span style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 20, fontWeight: 800, color: c }}>{F(t, plan.currency)}</span>
                          <span style={{ fontSize: 11, color: "#aaa", marginLeft: 2 }}>/{lbl}</span>
                          {period === 'annual' && rate > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${c}10`, color: c, fontWeight: 600 }}>
                                -{Math.round(rate * 100)}% · Éco. {F(sav, plan.currency)}/an
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {!zero && (
                        <button onClick={() => buy(plan)} disabled={payId === plan.id} style={{
                          width: "100%", padding: "8px 0", borderRadius: 7, border: "none",
                          background: `linear-gradient(135deg,${c},${c}cc)`, color: "white",
                          fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "'Outfit'",
                          boxShadow: `0 3px 12px ${c}22`, marginBottom: 8, transition: "opacity .2s",
                        }}
                          onMouseEnter={e => e.currentTarget.style.opacity = ".88"}
                          onMouseLeave={e => e.currentTarget.style.opacity = "1"}>
                          {payId === plan.id ? "Traitement…" : "💳 Paiement en ligne"}
                        </button>
                      )}

                      {plan.phone && (
                        <a href={`tel:${plan.phone}`} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8, color: "#888", fontSize: 10, textDecoration: "none" }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.82 16.43 14.93C17.55 15.3 18.75 15.5 20 15.5C20.55 15.5 21 15.95 21 16.5V20C21 20.55 20.55 21 20 21C10.61 21 3 13.39 3 4C3 3.45 3.45 3 4 3H7.5C8.05 3 8.5 3.45 8.5 4C8.5 5.25 8.7 6.45 9.07 7.57C9.18 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z" /></svg>
                          {plan.phone}
                        </a>
                      )}

                      {plan.features?.length > 0 && (
                        <div style={{ width: "100%", textAlign: "left", padding: 8, background: "#fafafa", borderRadius: 7, flex: 1 }}>
                          <div style={{ fontWeight: 700, color: "#333", marginBottom: 4, fontSize: 10 }}>Description de l'offre :</div>
                          {plan.features.map((f, j) => (
                            <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 3 }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill={c} style={{ flexShrink: 0, marginTop: 2 }}><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                              <span style={{ fontSize: 10.5, color: "#555", lineHeight: 1.35 }}>{f}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Garantie */}
          <div style={{ textAlign: "center", marginTop: 20, ...A("pricing") }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 20px", borderRadius: 10, background: "white", border: "1px solid #e5e7eb" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#52c41a"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" /></svg>
              <span style={{ fontSize: 12, color: "#555" }}><strong>Garantie 14 jours</strong> — Satisfait ou remboursé, sans condition.</span>
            </div>
          </div>

          {/* Info notice */}
          <div style={{ maxWidth: 700, margin: "16px auto 0", padding: "14px 20px", borderRadius: 10, background: "#fff8f0", border: "1px solid #f0dcc0", display: "flex", alignItems: "flex-start", gap: 10 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#E85D04" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
            </svg>
            <div style={{ fontSize: 12, color: "#555", lineHeight: 1.6 }}>
              <strong style={{ color: "#E85D04" }}>Important :</strong> La plupart de nos accès sont compatibles avec le navigateur français.
              Une fois le paiement reçu, la préparation de vos accès peut prendre jusqu'à <strong>48 heures</strong>. Vos identifiants vous seront transmis par courriel.
            </div>
          </div>
        </div>
      </section>

      {/* ==== TÉMOIGNAGES ==== */}
      <section id="testimonials" ref={R("testimonials")} style={{ padding: "44px 20px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 28, ...A("testimonials") }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#E85D04", letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>Témoignages</span>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 24, fontWeight: 800, color: "#111", margin: 0 }}>Ce que disent nos clients</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 14 }}>
            {[
              { name: "Karim B.", role: "Consultant SAP FI/CO", text: "Environnement stable et performant. J'ai pu pratiquer tous les modules sans limite. Le support est très réactif.", avatar: "K" },
              { name: "Sophie L.", role: "Formatrice SAP MM/SD", text: "Mes étudiants ont chacun leur accès dédié. La mise en place a été rapide et le support technique excellent.", avatar: "S" },
              { name: "Marc D.", role: "Développeur ABAP", text: "L'accès ABAP Workbench est complet. Je peux développer et tester mes programmes CDS Views sans restriction.", avatar: "M" },
            ].map((t, i) => (
              <div key={i} style={{ padding: 20, borderRadius: 10, background: "white", border: "1px solid #f0f0f0", ...A("testimonials", i) }}>
                <div style={{ display: "flex", gap: 2, marginBottom: 10 }}>
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} width="14" height="14" viewBox="0 0 24 24" fill="#faad14"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: "#555", lineHeight: 1.6, margin: "0 0 14px", fontStyle: "italic" }}>"{t.text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg,#0054A6,#00875A)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 13, fontWeight: 700 }}>{t.avatar}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#222" }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: "#999" }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==== FAQ ==== */}
      <section id="faq" ref={R("faq")} style={{ padding: "44px 20px", background: "#fafafa" }}>
        <div style={{ maxWidth: 580, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 24, ...A("faq") }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#E85D04", letterSpacing: 1.5, textTransform: "uppercase", display: "block", marginBottom: 6 }}>FAQ</span>
            <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 800, color: "#111", margin: 0 }}>Questions fréquentes</h2>
          </div>
          {[
            ["Combien de temps pour activer mon accès ?", "Votre environnement est provisionné sous 24h après paiement."],
            ["Puis-je personnaliser mon environnement ?", "Oui, créez vos données et configurez les modules librement."],
            ["Y a-t-il un engagement minimum ?", "Non, sans engagement. Annulez à tout moment."],
            ["Le support est-il inclus ?", "Oui, support technique inclus dans tous les plans."],
            ["Comment accéder à mon environnement ?", "Vous recevrez vos identifiants de connexion par email après activation."],
            ["Le paiement est-il sécurisé ?", "Oui, nous utilisons Moneris, une plateforme de paiement certifiée PCI DSS."],
          ].map(([q, a], i) => (
            <div key={i} style={{ borderRadius: 8, background: "white", border: "1px solid #eee", overflow: "hidden", marginBottom: 6, ...A("faq", i) }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: "100%", padding: "12px 16px", border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'Outfit'", textAlign: "left" }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: "#222" }}>{q}</span>
                <span style={{ fontSize: 14, color: "#bbb", transition: "transform .25s", transform: openFaq === i ? "rotate(45deg)" : "rotate(0)", flexShrink: 0, marginLeft: 10 }}>+</span>
              </button>
              <div style={{ maxHeight: openFaq === i ? 120 : 0, overflow: "hidden", transition: "max-height .3s ease" }}>
                <p style={{ padding: "0 16px 12px", margin: 0, fontSize: 12, color: "#777", lineHeight: 1.6 }}>{a}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ==== CTA ==== */}
      <section id="cta" ref={R("cta")} style={{ padding: "40px 20px", textAlign: "center", background: "#0a1628" }}>
        <div style={{ maxWidth: 480, margin: "0 auto", ...A("cta") }}>
          <h2 style={{ fontFamily: "'Plus Jakarta Sans'", fontSize: 22, fontWeight: 800, color: "white", margin: "0 0 8px" }}>
            Prêt à démarrer votre <span style={{ color: "#FF8C00" }}>parcours SAP</span> ?
          </h2>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,.35)", margin: "0 0 20px" }}>Rejoignez des centaines de professionnels sur Nomidya.</p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="#pricing" style={{ padding: "10px 24px", borderRadius: 8, background: "#E85D04", color: "white", fontWeight: 700, fontSize: 13, textDecoration: "none" }}>Choisir mon plan</a>
            <a href="tel:+14188096316" style={{ padding: "10px 24px", borderRadius: 8, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.6)", fontWeight: 600, fontSize: 13, textDecoration: "none" }}>📞 1 (418) 809 6316</a>
          </div>
        </div>
      </section>

      {/* ==== FOOTER ==== */}
      <footer style={{ padding: "24px 20px", background: "#060d16", textAlign: "center" }}>
        <img src="/img/nomidya.png" alt="Nomidya" style={{ height: 36, marginBottom: 8, opacity: .8 }} />
        <p style={{ fontSize: 10, color: "rgba(255,255,255,.2)", margin: 0 }}>© 2026 Nomidya — La Carrière. Tous droits réservés.</p>
      </footer>

      {/* ==== WHATSAPP ==== */}
      <a href="https://wa.me/14188096316?text=Bonjour%2C%20je%20suis%20int%C3%A9ress%C3%A9%20par%20un%20acc%C3%A8s%20SAP%20S%2F4%20HANA"
        target="_blank" rel="noopener noreferrer" title="Contactez-nous sur WhatsApp"
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 1500,
          width: 52, height: 52, borderRadius: "50%",
          background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 16px rgba(37,211,102,.35)", transition: "transform .2s", cursor: "pointer",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {/* ==== MODALS ==== */}
      <FocusModal visible={cfmVis} onClose={() => { setCfmVis(false); setSel(null); setPayId(null); }} title="Confirmer votre achat">
        {sel && (() => {
          const { t } = calc(sel, period);
          return (
            <div style={{ padding: 14, background: "#f9fafb", borderRadius: 8, marginBottom: 14, textAlign: "center" }}>
              <div style={{ color: gc(sel, 0), fontSize: 13, fontWeight: 700 }}>{sel.title}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: gc(sel, 0), marginTop: 4, fontFamily: "'Plus Jakarta Sans'" }}>{F(t, sel.currency)} / {LBL[period]}</div>
            </div>
          );
        })()}
        <p style={{ textAlign: "center", marginBottom: 14, color: "#888", fontSize: 12 }}>Cliquez sur « Procéder au paiement » pour continuer.</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => { setCfmVis(false); setSel(null); setPayId(null); }} style={{ padding: "8px 16px", background: "#f0f0f0", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12 }}>Annuler</button>
          <button onClick={cfm} style={{ padding: "8px 18px", background: "#E85D04", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>Procéder au paiement</button>
        </div>
      </FocusModal>

      <PaymentWrapper visible={payVis} course={payData} onSuccess={onOk} onError={onErr} onCancel={onX} />

      <style>{`
        *{box-sizing:border-box;margin:0} html{scroll-behavior:smooth}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:1200px){div.lp-plans-grid{grid-template-columns:repeat(3,1fr)!important}}
        @media(max-width:768px){div.lp-plans-grid{grid-template-columns:repeat(2,1fr)!important}}
        @media(max-width:480px){div.lp-plans-grid{grid-template-columns:1fr!important;max-width:100%!important}}
    `}</style>
    </div>
  );
};

export default SapLandingPage;